<template>
  <div class="space-y-2">
    <div class="flex items-center justify-between">
      <Label class="text-sm font-medium">
        Brightness
        <span v-if="selectedCount > 0" class="text-muted-foreground ml-1">
          ({{ selectedCount }} {{ selectedCount === 1 ? 'light' : 'lights' }})
        </span>
      </Label>
      <span class="text-sm text-muted-foreground">{{ brightnessPercent }}%</span>
    </div>
    
    <Slider
      :model-value="[brightnessValue]"
      :min="0"
      :max="255"
      :step="1"
      :disabled="disabled || selectedCount === 0"
      @update:model-value="handleBrightnessChange"
      class="w-full"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { useLightsStore } from '@/stores/lights'
import type { Light } from '@/stores/lights'

interface Props {
  selectedItems: Light[]
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false
})

const emit = defineEmits<{
  brightnessChange: [value: number]
}>()

const lightsStore = useLightsStore()

const selectedCount = computed(() => props.selectedItems.length)

// Get average brightness from selected lights
const brightnessValue = computed(() => {
  if (props.selectedItems.length === 0) return 127
  
  let total = 0
  let count = 0
  
  for (const item of props.selectedItems) {
    const brightness = item.current_state?.bri ?? 127
    total += brightness
    count++
  }
  
  return count > 0 ? Math.round(total / count) : 127
})

const brightnessPercent = computed(() => {
  return Math.round((brightnessValue.value / 255) * 100)
})

const handleBrightnessChange = (value: number[] | undefined) => {
  if (value && value.length > 0) {
    emit('brightnessChange', value[0])
  }
}
</script>
